/* @flow */

import type { ViewMode } from "shop-state/types";

import React, { useEffect } from "react";
import cn from "classnames";
import { Link, useHistory } from "react-router-dom";
import { Button } from "@crossroads/ui-components";
import styles from "./styles.scss";
import Wrapper from "components/Wrapper";
import CloseIcon from "icons/cross.svg";
import ChevronIcon from "icons/chevron.svg";
import { useTranslate } from "@awardit/react-use-translate";

export type Item = {
  name: string,
  url: string,
};

type Props = {
  setViewMode?: ViewMode => void,
  setHamburgerOpen?: boolean => void,
  className?: string,
  onClose: () => void,
  onBack?: () => void,
  closeText: string,
  heading: string,
  items: Array<Item>,
  useFocus?: boolean,
};

type ItemProps = {
  className: string,
  item: Item,
  children: React$Node,
};

const Drawer: React$AbstractComponent<Props, HTMLDivElement> =
React.forwardRef<Props, HTMLDivElement>(({
  setViewMode,
  setHamburgerOpen,
  items,
  heading,
  closeText,
  onClose,
  className = "",
  onBack,
  useFocus = false,
}: Props, ref) => {
  const history = useHistory();
  const t = useTranslate();

  useEffect(() => {
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", closeOnEscape);

    return () =>
      window.removeEventListener("keydown", closeOnEscape);
  }, []);

  useEffect(() => {
    if (useFocus && ref && typeof ref !== "function" && ref.current !== null) {
      ref.current.querySelector("button")?.focus();
    }
  }, [ref, useFocus]);

  const ItemLink = ({ item, className = styles.item, children }: ItemProps) => {
    const isParent = history.location.pathname === item.url;

    return item.url === "/" ? (
      <div className={className}>
        {children}
      </div>
    ) : (
      <Link
        to={{
          pathname: item.url,
          state: { hint: { type: "category", category: item } },
        }}
        className={className}
        onClick={() => {
          if (isParent) {
            if (setViewMode) {
              setViewMode("normal");
            }
            else if (setHamburgerOpen) {
              setHamburgerOpen(false);
            }
          }
        }}
      >
        {children}
      </Link>
    );
  };

  return (
    <div ref={ref} className={cn(styles.block, className)}>
      <div className={styles.row}>
        <Wrapper className={styles.top}>
          {onBack &&
            <Button className={styles.back} aria-label={t("HEADER.BACK")} onClick={onBack}>
              <ChevronIcon aria-label={t("ICONS.ARROW")} />
            </Button>
          }
          <span className={styles.heading}>{heading}</span>
          <Button
            id="drawerClose"
            className={cn(styles.close, styles.actionButton)}
            onClick={onClose}
          >
            <span className={styles.closeText}>{closeText}</span>
            <CloseIcon aria-label={t("ICONS.CROSS")} />
          </Button>
        </Wrapper>
      </div>

      <Wrapper>
        <div className={styles.wrapper}>
          {items.map(x => (
            <ItemLink key={x.name} item={x} className={styles.item}>
              <div className={styles.item__heading}>{x.name}</div>
            </ItemLink>
          ))}
        </div>
      </Wrapper>
    </div>
  );
});

export default Drawer;
